"use client"

import { useEffect, useRef, type RefObject } from 'react'

const PANEL_SELECTOR = '[class*="entity-chat-panel-base"]'

// Whether the page-wide message panel is currently open. Module-global on purpose: the panel is a single
// document-wide element, but each trigger's MutationObserver only sees mutations in its OWN subtree. A
// per-trigger "last seen" cache goes stale on a switch — open A, switch to B, close B (which never mutates
// A's subtree), reopen A — so A would still read "open", skip the reopen, and leave the grid unlocked
// behind the visible panel. One shared value, reconciled by whichever trigger observes a transition, keeps
// the dedup (no churn on unrelated icon/badge childList mutations) without going stale.
let lastGlobalPanelOpen: boolean | null = null

/** Test-only: reset the shared open/closed dedup between cases. */
export function __resetSidebarPanelObserverStateForTests() {
  lastGlobalPanelOpen = null
}

/**
 * Watch `triggerRef`'s own subtree for the page-wide message panel opening or closing, and call `onChange`
 * on each real transition of the panel's open state.
 *
 * The panel renders INLINE inside the opening trigger's subtree (fixed-positioned, not portaled to
 * `<body>`), so observing the trigger's subtree — not `<body>` — is what reliably catches its removal on
 * close. `onChange` is read through a ref, so a fresh inline closure each render does not tear down and
 * recreate the observer.
 */
export function useSidebarPanelObserver(
  triggerRef: RefObject<HTMLElement | null>,
  onChange: (isOpen: boolean) => void
) {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Per-instance: has THIS hook ever fired onChange (null = not yet). The module-global above dedups
  // transitions across triggers, but on its own it means an instance that mounts while the panel already
  // matches the global never learns its initial state — violating the "call onChange for this consumer"
  // contract for any future per-instance consumer. So each instance force-notifies on its first check,
  // even when the state already matches the global; the global still suppresses churn thereafter.
  const hasNotifiedRef = useRef<boolean | null>(null)

  useEffect(() => {
    const triggerEl = triggerRef.current

    const checkSidebarState = () => {
      const messagePanel = document.querySelector(PANEL_SELECTOR)
      const isOpen = Boolean(messagePanel && getComputedStyle(messagePanel).display !== 'none')
      const isFirstCheck = hasNotifiedRef.current === null
      if (isOpen === lastGlobalPanelOpen && !isFirstCheck) return
      lastGlobalPanelOpen = isOpen
      hasNotifiedRef.current = isOpen
      onChangeRef.current(isOpen)
    }

    checkSidebarState()
    if (!triggerEl) return

    const observer = new MutationObserver(checkSidebarState)
    observer.observe(triggerEl, { childList: true, subtree: true })
    return () => observer.disconnect()
    // Set up once per mount; onChange flows through onChangeRef so a new closure does not re-run this.
  }, [triggerRef])
}
